import classNames from 'classnames';
import { isFuture } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Gift } from 'atoms/icons/Gift';
import { Timer } from 'atoms/icons/Timer';
import { Horizontal } from 'atoms/layout/flex';
import { BodyS } from 'atoms/typography';
import { LiveDot } from 'components/live/LiveDot';
import { ShortTimeLeft } from 'contexts/ticker/ShortTimeLeft';
import { TimeLeft } from 'contexts/ticker/TimeLeft';
import { navLabels } from 'lib/glossary';
import { FixtureState } from 'lib/so5';

const Root = styled(Horizontal).attrs({ gap: 0.5 })`
  padding: var(--half-unit) var(--unit);
  border-radius: var(--unit);
  backdrop-filter: blur(2px);
  white-space: nowrap;

  &.opened {
    color: var(--c-yellow-400);
    background: rgba(240, 201, 86, 0.16);
  }
  &.live {
    background: rgba(245, 103, 87, 0.16);
    &.onLightBackground {
      background: var(--c-red-400);
    }
  }
  &.preparing {
    color: var(--c-blue-300);
    background: rgba(var(--c-rgb-blue-300), 0.3);
  }
  &.waitingForRewards {
    background: rgba(61, 220, 132, 0.16);
    color: var(--c-rivals-win);
  }
`;

type CommonProps = {
  shortMessage?: boolean;
  onLightBackground?: boolean;
};

type Props = CommonProps &
  (
    | {
        fixtureState: FixtureState.started;
      }
    | {
        fixtureState: FixtureState.closed;
        expectedRewardsDistributionDate?: Nullable<Date>;
      }
    | {
        fixtureState: FixtureState.opened;
        startDate: Date;
      }
  );

export const FixtureStatus = (props: Props) => {
  const { fixtureState, onLightBackground, shortMessage = false } = props;

  const TimeLeftComponent = shortMessage ? ShortTimeLeft : TimeLeft;

  if (fixtureState === FixtureState.opened) {
    const { startDate } = props;
    return (
      <Root className="opened">
        <Timer size={2} />
        <BodyS as="p" bold color="var(--c-white)">
          <TimeLeftComponent time={startDate} />
        </BodyS>
      </Root>
    );
  }
  if (fixtureState === FixtureState.started) {
    const textColor = onLightBackground ? 'var(--c-white)' : 'var(--c-red-400)';
    return (
      <Root
        className={classNames('live', {
          onLightBackground,
        })}
      >
        <LiveDot animate color={textColor} />
        <BodyS as="p" bold color={textColor}>
          <FormattedMessage {...navLabels.live} />
        </BodyS>
      </Root>
    );
  }
  if (fixtureState === FixtureState.closed) {
    const { expectedRewardsDistributionDate } = props;
    if (
      expectedRewardsDistributionDate &&
      isFuture(expectedRewardsDistributionDate)
    ) {
      return (
        <Root className="waitingForRewards">
          <Gift size={1.5} />
          <BodyS as="p" bold color="var(--c-white)">
            <TimeLeftComponent time={expectedRewardsDistributionDate} />
          </BodyS>
        </Root>
      );
    }
  }

  return null;
};
